import { FC, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { getTraceById, getFileContent } from '../../services/feeTracingService';

interface FeesEmulatorTransactionLookupProps {
  onLoad: (payloadJson: string) => void;
  onError: (message: string) => void;
}

/**
 * Compact widget that loads a fee request payload by transaction id. Looks up the trace logs for
 * the given transaction, finds the entry whose EventData captures the inbound request, and pulls
 * the JSON content into the parent's editor.
 */
export const FeesEmulatorTransactionLookup: FC<FeesEmulatorTransactionLookupProps> = ({ onLoad, onError }) => {
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLoad = async () => {
    const id = transactionId.trim();
    if (!id) return;
    setLoading(true);
    try {
      const traceLogs = await getTraceById(id);
      // The inbound request is captured as EventData on a trace log; pick the first entry that has one.
      const requestLog = traceLogs.find(log => !!log.eventDataFileId);
      if (!requestLog?.eventDataFileId) {
        onError(`No request payload found for transaction ${id}`);
        return;
      }
      const content = await getFileContent(requestLog.eventDataFileId);
      // EventData is JSON; reformat for editor readability.
      try {
        const parsed = JSON.parse(content);
        // Multi-product traces capture productInformation as an array; the emulator endpoint takes
        // a single product. Unwrap to the first product so the loaded payload is runnable here.
        if (Array.isArray(parsed?.productInformation)) {
          parsed.productInformation = parsed.productInformation[0] ?? {};
        }
        onLoad(JSON.stringify(parsed, null, 2));
      } catch {
        onLoad(content);
      }
    } catch (err: any) {
      onError(err?.message ?? 'Failed to load transaction payload');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-inputgroup" style={{ marginBottom: '0.5rem' }}>
      <InputText
        placeholder="Transaction ID"
        value={transactionId}
        onChange={e => setTransactionId(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleLoad()}
        disabled={loading}
      />
      <Button
        label="Load"
        icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
        onClick={handleLoad}
        disabled={loading || !transactionId.trim()}
      />
    </div>
  );
};

export default FeesEmulatorTransactionLookup;
